import { ENV } from '../../config/runtimeEnv';
import {
  TreatmentPlanTemplate,
  PatientTreatmentPlan,
  PatientTreatmentPlanStageLog,
  TreatmentNoteTemplate,
  TreatmentNote,
  PendingDashboardData,
} from '../../types/healthcare/TreatmentPlanTypes';
import { PatientAnalysis } from '../../types/healthcare/PatientAnalysisTypes';
import type { UserUuid } from '../../types/identity';
import { http } from '../../lib/http/client';

const BASE = ENV.VITE_HEALTHCARE_SERVER;

// ── Templates ────────────────────────────────────────────────────────────

export const fetchTemplates = async (): Promise<TreatmentPlanTemplate[]> => {
  const res = await http.get(`${BASE}/practitioner/treatment-plans`, {
    params: { include_inactive: true },
  });
  return res.data.data;
};

export const fetchTemplate = async (id: number): Promise<TreatmentPlanTemplate> => {
  const res = await http.get(`${BASE}/practitioner/treatment-plans/${id}`);
  return res.data.data;
};

export type TemplatePayload = Omit<Partial<TreatmentPlanTemplate>, 'stages' | 'connections' | 'sub_plans'> & {
  stages?: Record<string, any>[];
  connections?: Record<string, any>[];
  sub_plans?: Record<string, any>[];
};

export const createTemplate = async (data: TemplatePayload): Promise<TreatmentPlanTemplate> => {
  const res = await http.post(`${BASE}/practitioner/treatment-plans`, data);
  return res.data.data;
};

export const updateTemplate = async (
  id: number,
  data: TemplatePayload
): Promise<TreatmentPlanTemplate> => {
  const res = await http.put(`${BASE}/practitioner/treatment-plans/${id}`, data);
  return res.data.data;
};

export const deleteTemplate = async (id: number): Promise<void> => {
  await http.delete(`${BASE}/practitioner/treatment-plans/${id}`);
};

export const duplicateTemplate = async (id: number): Promise<TreatmentPlanTemplate> => {
  const res = await http.post(`${BASE}/practitioner/treatment-plans/${id}/duplicate`, {});
  return res.data.data;
};

// ── Patient Plans ────────────────────────────────────────────────────────

export const assignPlan = async (payload: {
  patient_id: UserUuid;
	practitioner_id?: UserUuid;
  treatment_plan_template_id: number;
}): Promise<PatientTreatmentPlan> => {
  const res = await http.post(`${BASE}/practitioner/patient-treatment-plans/assign`, payload);
  return res.data.data;
};

export const fetchPatientPlans = async (patientId: UserUuid): Promise<PatientTreatmentPlan[]> => {
  const res = await http.get(`${BASE}/practitioner/patient-treatment-plans/patient/${patientId}`);
  return res.data.data;
};

export const fetchCompanyPlans = async (
  params?: { status?: string; page?: number; per_page?: number }
): Promise<{ data: PatientTreatmentPlan[]; total: number; per_page: number; current_page: number }> => {
  const res = await http.get(`${BASE}/practitioner/patient-treatment-plans/company`, {
    params,
  });
  return res.data;
};

export const fetchPlan = async (id: number): Promise<PatientTreatmentPlan> => {
  const res = await http.get(`${BASE}/practitioner/patient-treatment-plans/${id}`);
  return res.data.data;
};

export const advanceStage = async (
  planId: number,
  stageLogId: number,
  outcome?: string,
  outcomeData?: Record<string, any>,
  practitioner_id?: UserUuid,
  optionKey?: string,
): Promise<PatientTreatmentPlan> => {
  const res = await http.post(
    `${BASE}/practitioner/patient-treatment-plans/${planId}/advance`,
    { stage_log_id: stageLogId, outcome, outcome_data: outcomeData, practitioner_id, option_key: optionKey }
  );
  return res.data.data;
};

export const selectSubPlans = async (
  planId: number,
  stageLogId: number,
  templateIds: number[],
  practitionerId: UserUuid,
): Promise<PatientTreatmentPlan> => {
  const res = await http.post(
    `${BASE}/practitioner/patient-treatment-plans/${planId}/select-sub-plans`,
    { stage_log_id: stageLogId, template_ids: templateIds, practitioner_id: practitionerId }
  );
  return res.data.data;
};

export const pausePlan = async (planId: number): Promise<PatientTreatmentPlan> => {
  const res = await http.post(
    `${BASE}/practitioner/patient-treatment-plans/${planId}/pause`,
    {}
  );
  return res.data.data;
};

export const resumePlan = async (planId: number): Promise<PatientTreatmentPlan> => {
  const res = await http.post(
    `${BASE}/practitioner/patient-treatment-plans/${planId}/resume`,
    {}
  );
  return res.data.data;
};

export const cancelPlan = async (planId: number): Promise<PatientTreatmentPlan> => {
  const res = await http.post(
    `${BASE}/practitioner/patient-treatment-plans/${planId}/cancel`,
    {}
  );
  return res.data.data;
};

export const skipStage = async (
  planId: number,
  stageLogId: number,
  practitionerId: UserUuid
): Promise<void> => {
  await http.post(
    `${BASE}/practitioner/patient-treatment-plans/${planId}/skip-stage`,
    { stage_log_id: stageLogId, practitioner_id: practitionerId }
  );
};

export const triggerComprehensiveAnalysis = async (
  planId: number,
  stageLogId: number,
  extraContext: string | null
): Promise<void> => {
  await http.post(
    `${BASE}/practitioner/patient-treatment-plans/${planId}/trigger-analysis`,
    { stage_log_id: stageLogId, extra_context: extraContext }
  );
};

export const checkBloodResults = async (
  planId: number,
  stageLogId: number
): Promise<{ missing: string[] }> => {
  const res = await http.post(
    `${BASE}/practitioner/patient-treatment-plans/${planId}/check-blood-results`,
    { stage_log_id: stageLogId }
  );
  return res.data;
};

export const fetchPlanStageLog = async (planId: number): Promise<PatientTreatmentPlanStageLog[]> => {
  const res = await http.get(`${BASE}/practitioner/patient-treatment-plans/${planId}/log`);
  return res.data.data;
};

// ── Note Templates ───────────────────────────────────────────────────────

export const fetchNoteTemplates = async (): Promise<TreatmentNoteTemplate[]> => {
  const res = await http.get(`${BASE}/practitioner/treatment-note-templates`, {
    params: { include_inactive: true },
  });
  return res.data.data;
};

export const fetchNoteTemplate = async (id: number): Promise<TreatmentNoteTemplate> => {
  const res = await http.get(`${BASE}/practitioner/treatment-note-templates/${id}`);
  return res.data.data;
};

export const createNoteTemplate = async (data: Partial<TreatmentNoteTemplate>): Promise<TreatmentNoteTemplate> => {
  const res = await http.post(`${BASE}/practitioner/treatment-note-templates`, data);
  return res.data.data;
};

export const updateNoteTemplate = async (id: number, data: Partial<TreatmentNoteTemplate>): Promise<TreatmentNoteTemplate> => {
  const res = await http.put(`${BASE}/practitioner/treatment-note-templates/${id}`, data);
  return res.data.data;
};

export const deleteNoteTemplate = async (id: number): Promise<void> => {
  await http.delete(`${BASE}/practitioner/treatment-note-templates/${id}`);
};

// ── Treatment Notes ──────────────────────────────────────────────────────

export const fetchPatientNotes = async (planId: number): Promise<TreatmentNote[]> => {
  const res = await http.get(`${BASE}/practitioner/treatment-notes/plan/${planId}`);
  return res.data.data;
};

export const fetchNotesByAppointment = async (appointmentId: number): Promise<TreatmentNote[]> => {
  const res = await http.get(`${BASE}/practitioner/treatment-notes/appointment/${appointmentId}`);
  return res.data.data;
};

export const createNote = async (data: Partial<TreatmentNote>): Promise<TreatmentNote> => {
  const res = await http.post(`${BASE}/practitioner/treatment-notes`, data);
  return res.data.data;
};

export const updateNote = async (id: number, data: Partial<TreatmentNote>): Promise<TreatmentNote> => {
  const res = await http.put(`${BASE}/practitioner/treatment-notes/${id}`, data);
  return res.data.data;
};

// ── Practitioner Tasks ───────────────────────────────────────────────────

export const completePractitionerTask = async (
  planId: number,
  stageLogId: number,
  practitionerId: UserUuid,
  taskPayload: Record<string, any>,
): Promise<PatientTreatmentPlan> => {
  const res = await http.post(
    `${BASE}/practitioner/patient-treatment-plans/${planId}/complete-task`,
    { stage_log_id: stageLogId, practitioner_id: practitionerId, task_payload: taskPayload },
  );
  return res.data.data;
};

export const reviewPatientAnalysis = async (
  planId: number,
  stageLogId: number,
  practitionerId: UserUuid,
): Promise<PatientTreatmentPlan> => {
  const res = await http.post(
    `${BASE}/practitioner/patient-treatment-plans/${planId}/review-analysis`,
    { stage_log_id: stageLogId, practitioner_id: practitionerId },
  );
  return res.data.data;
};
export const fetchPendingDashboard = async (
  includeCancelled = false,
): Promise<PendingDashboardData> => {
  const res = await http.get(`${BASE}/practitioner/patient-treatment-plans/pending-dashboard`, {
    params: {
      ...(includeCancelled ? { include_cancelled: 1 } : {}),
    },
  });
  return res.data.data;
};

export const fetchPlanComprehensiveAnalysis = async (
  planId: number,
): Promise<PatientAnalysis | null> => {
  try {
    const res = await http.get(
      `${BASE}/practitioner/patient-treatment-plans/${planId}/comprehensive-analysis`,
    );
    return res.data.data;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

// ── Approved Pharmacies ──────────────────────────────────────────────────────

export interface ApprovedPharmacy {
  id: number;
  name: string;
  code: string;
  abn: string | null;
  licence_no: string | null;
  address: string | null;
}

export const fetchApprovedPharmacies = async (): Promise<ApprovedPharmacy[]> => {
  const res = await http.get(`${BASE}/practitioner/approved-pharmacies`);
  return res.data.data;
};
